import {
  useState, useMemo, useEffect, useRef,
} from 'react';
import { useAsyncCallback } from './use-async-callback';

/** `useDataSource` is a React hook to retrieve data from the backend. This happens when the component is first loaded, and when the refresh function is called.
 * @param {function} retrievalFunction - A parameterless function to retrieve data from the backend.
 * @returns {[ data, refresh: func ]} - `data` is the value asynchronously returned by retrievalFunction. `refresh` is a parameterless function to refresh the data from the backend.
 */
export const useDataSource = (retrievalFunction, dependencies = []) => {
  const [data, setter] = useState(null);
  const isUnmounted = useRef(false);

  const refresh = useAsyncCallback(async (...args) => {
    const retrievedData = await retrievalFunction(...args);
    if (!isUnmounted.current) {
      setter(retrievedData);
    }
  }, [retrievalFunction]);

  useEffect(() => {
    isUnmounted.current = false;
    refresh();

    return () => {
      isUnmounted.current = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh, ...dependencies]);

  return useMemo(() => ([
    data,
    refresh,
  ]), [data, refresh]);
};
